import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import RemitoPrintView from './RemitoPrintView';

interface RemitoItem {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface Remito {
  id: string;
  remitNumber: string;
  clientId: string;
  clientName: string;
  companyName?: string;
  vehicleInfo?: string;
  licensePlate?: string;
  type: 'service' | 'delivery' | 'warranty' | 'internal';
  serviceType?: string;
  observations?: string;
  status: 'draft' | 'issued' | 'delivered' | 'cancelled';
  totalQuantity: number;
  estimatedValue: number;
  issuedAt?: string;
  deliveredAt?: string;
  receivedBy?: string;
  createdAt: string;
  createdByName: string;
  relatedBudgetId?: string;
  relatedAppointmentId?: string;
  paymentMethod?: string;
  paymentInterval?: number;
  items: RemitoItem[];
}

const RemitoPDFView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [remito, setRemito] = useState<Remito | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRemito = async () => {
      if (!id) {
        setError('ID de remito no proporcionado');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Verificar acceso autorizado
        const userAgent = navigator.userAgent;
        const isPuppeteer = userAgent.includes('HeadlessChrome') || 
                           userAgent.includes('Puppeteer') ||
                           userAgent.includes('Chrome-Lighthouse');
        
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
        
        // Permitir acceso: Puppeteer (interno), localhost (dev), o usuario logueado (token)
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (!isPuppeteer && !isLocalhost && !token) {
          setError('Acceso no autorizado');
          return;
        }

        // Para Puppeteer no hace falta token; para el resto lo enviamos si lo tenemos
        let headers = {};
        if (!isPuppeteer && token) {
          headers = {
            'Authorization': `Bearer ${token}`
          };
        }

        const response = await axios.get(`/api/remitos/${id}/pdf-data`, {
          headers
        });

        console.log('📊 Datos recibidos del backend:', response.data);
        console.log('📦 Items recibidos:', response.data.items?.length || 0);
        console.log('💰 Valor total recibido:', response.data.estimatedValue);
        console.log('💳 Total contado recibido:', response.data.totalContado);

        setRemito(response.data);
      } catch (error: any) {
        console.error('Error fetching remito:', error);
        setError(error.response?.data?.error || 'Error al cargar el remito');
      } finally {
        setLoading(false);
      }
    };

    fetchRemito();
  }, [id]);

  if (loading) {
    return (
      <div className="pdf-mode min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-mode min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!remito) {
    return (
      <div className="pdf-mode min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">Remito no encontrado</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-mode">
      <RemitoPrintView 
        remito={remito} 
        onClose={() => {}} // Función vacía ya que no necesitamos cerrar en modo PDF
      />
    </div>
  );
};

export default RemitoPDFView;
